/**
 * Module registry — the unit of "what this client bought".
 *
 * A module is a coherent slice of product (Leads, Deals, Inbox…). A tenant's
 * plan enables a set of module ids; everything downstream — navigation, routes,
 * command palette, dashboard widgets, global search — is derived from that set
 * rather than hard-coded. Adding a module means adding one entry here.
 */

export const MODULE_IDS = [
  'dashboard',
  'leads',
  'contacts',
  'companies',
  'deals',
  'activities',
  'tasks',
  'inbox',
  'mail',
  'calls',
  'calendar',
  'reports',
  'automations',
  'agents',
  'escalation',
  'usage',
  'traceability',
  'bases',
  'tickets',
  'settings',
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

/**
 * Los **sistemas**: los servicios de Ribox que la organización contrata de
 * verdad. La raíz del árbol de dependencias, no una preferencia más.
 *
 * Un módulo apagado se enciende con un clic. Un módulo sin su sistema no se
 * puede encender, y eso es lo que dice esta lista: sin telefonía no hay dialer
 * que llame, y sin WhatsApp no hay agente de texto que conteste. La diferencia
 * importa al dar de alta a un cliente — se parte por lo mínimo, y la pantalla
 * misma tiene que hacer que las combinaciones imposibles no se puedan dibujar.
 *
 * Espejado en `backend/api/v2/tenant_mapping.py` (`SYSTEM_IDS`), con una prueba
 * que falla si divergen.
 */
export const SYSTEM_IDS = ['phone', 'whatsapp'] as const;

export type SystemId = (typeof SYSTEM_IDS)[number];

/** Capabilities are finer than modules: a client can have Leads without import. */
export const CAPABILITY_IDS = [
  'leads.import',
  'leads.export',
  'leads.bulk_edit',
  'leads.kanban',
  'deals.forecast',
  'contacts.merge',
  'automations.run',
  'automations.reply',
  'reports.custom',
  'settings.custom_fields',
  'settings.audit_log',
  //
  // Acá estaba `settings.api_keys`, y se retiró con la pantalla que la usaba:
  // «Accesos» fabricaba el secreto con `crypto.getRandomValues()` en el
  // navegador y no autenticaba nada, así que la llave no abría ninguna puerta
  // que existiera. Una capacidad sin nada que habilitar es una casilla de
  // Funciones que promete funcionalidad que no hay; el id vuelve el día que
  // existan llaves de verdad. Ver `features/settings/sections.ts`, donde estaba
  // la sección.
  //
  // Ver qué está conectado y desconectarlo. Vive en Ajustes como las de arriba
  // y es otra cosa: gobierna casillas de correo que autorizó una persona con su
  // cuenta personal.
  // Acá estuvo un rato `settings.users`, que iba a apagar Ajustes → Usuarios por
  // organización. El dueño lo corrigió el 2026-09-24: «everyone should be able
  // to open usuarios, and only admins should be able to edit, delete and add
  // users (company admins)». Administrar las cuentas propias es de toda
  // organización; lo que se reparte es quién de adentro lo hace, y eso es el
  // permiso `config.usuarios` y no una capacidad. Una llave acá habría apagado
  // también la lectura, que es justo lo que el dueño quiere abierto.
  'settings.integrations',
  'inbox.send',
  // Seguir si abrieron un correo que mandamos, con una imagen de 1×1 en el
  // cuerpo. Es la primera capacidad del módulo Correo y la única que NO se
  // enciende sola (ver `CAPACIDADES_SIN_DEFECTO`): mide a alguien que no es
  // cliente nuestro, y eso lo decide cada organización por escrito.
  'mail.tracking',
  'calls.trigger',
  // Que el bot de la reunión grabe VIDEO además del audio. El archivo es
  // nuestro y no lo devuelve ninguna ruta del CRM: la ficha sigue oyendo el
  // audio, igual que hoy, así que esta llave no dibuja ni esconde nada de la
  // pantalla. Su interruptor vive en Ajustes › Módulos, que es `platformOnly`,
  // y NO se enciende sola (`CAPACIDADES_SIN_DEFECTO`).
  'calendar.recordVideo',
  // Que el CRM sume al bot de notas (`notas@ribox.cl`) a la invitación de las
  // reuniones que organiza alguien de afuera, para que entre a su Meet sin que
  // lo admitan (dueño, 2026-11-02). Es escribir en el calendario de un cliente
  // —ve al bot entre sus invitados— y pedirle a cada persona el permiso de
  // cambiar sus eventos: NO se enciende sola (`CAPACIDADES_SIN_DEFECTO`). La
  // pantalla la comprueba para pedir que se reconecte el calendario que todavía
  // no tiene ese permiso.
  'calendar.inviteBot',
  // Pausar o activar un agente. La única escritura del módulo que no tenía
  // llave: lanzar pide `calls.trigger` y conversar pide `personas.create`, y
  // mover el interruptor de estado no pedía nada. NO se enciende sola
  // (`CAPACIDADES_SIN_DEFECTO`): qué agente corre es nuestro, no del cliente.
  'agents.manage',
  // El modelo de personas, una llave por acción (decisión 17 del plan del
  // modelo único). El SERVIDOR las comprueba; acá sólo se dibuja o no el
  // control, que es lo que una pantalla puede decidir.
  'personas.create',
  'personas.edit',
  // Responsable y etiquetas del CASO (PR-7). Aparte de `personas.edit` porque
  // son otra decisión y muchas veces de otra persona: quién trabaja un caso lo
  // reparte quien lidera el equipo, y el catálogo de etiquetas lo gobierna quien
  // administra la cuenta.
  'personas.assign',
  'personas.tag',
  // Mover de etapa (PR-8). Aparte de `personas.edit` porque avanzar a alguien en
  // el embudo es la acción que el resto de la plataforma mira, y es la que un
  // día va a disparar automatizaciones: hoy el servidor sólo REPORTA lo que
  // habría disparado, y esa decisión tiene que poder darse por separado.
  'personas.stage',
  // EL WHATSAPP PERSONAL de la ficha: cada persona del equipo conecta SU número
  // y escribe desde él. **No es el WhatsApp del agente** —eso es la Bandeja, con
  // `inbox` e `inbox.send`— y por eso es otra llave (dueño, 2026-09-24): «we
  // dont want to allow personal whatsapp connections, but we do want the
  // whatsapp agent and to view the messages». NO se enciende sola.
  'personas.whatsapp',
  // EDITAR EL NOMBRE Y EL TELÉFONO de una persona (dueño, 2026-10-04). Aparte de
  // `personas.edit` porque no es corregir un correo: el id de la persona se
  // calcula con esos dos datos, y donde hay un espejo del operario el sistema
  // del cliente vuelve a escribir el nombre en la corrida siguiente. Sin esta
  // llave el servidor contesta 422 y la ficha muestra los dos campos de sólo
  // lectura, como siempre. NO se enciende sola (`CAPACIDADES_SIN_DEFECTO`).
  'personas.identity',
] as const;

export type CapabilityId = (typeof CAPABILITY_IDS)[number];

/**
 * El TERCER ESTADO, dicho una sola vez para todo el registro.
 *
 * Estaba escrito nada más que para los módulos, y es la misma idea en las cuatro
 * listas: hay tres maneras de que algo no se vea y sólo una es una decisión del
 * cliente.
 *
 * - **Apagado** es del CLIENTE: éste no lo compró y aquél sí. Vive en el dato de
 *   cada organización y se revierte con un clic.
 * - `unavailable` es **no construido**: no hay nada que encender. El id se
 *   escribe igual para que el día que exista sea una línea menos, y para que
 *   nadie lo reutilice mientras tanto.
 * - `retirado` es **ya no se ofrece**: existió, funcionó, y el producto dejó de
 *   tenerlo. El código sigue en el árbol.
 *
 * Los dos últimos son decisiones del PRODUCTO y por eso viven acá, en una línea,
 * y no en la configuración de cada organización: retirar algo editando los datos
 * de N tenants deja el retiro a medias en cuanto aparece el tenant N+1, y volver
 * atrás sería otra vez N ediciones. Acá volver atrás es borrar la línea.
 *
 * Y JUSTAMENTE POR ESO NO SE TOCA LA CONFIGURACIÓN DE NADIE: el id sigue escrito
 * en la organización, el servidor lo sigue viendo, y lo que alimente en otras
 * pantallas sigue funcionando igual. Lo único que desaparece es la superficie.
 */
export interface EstadoDeCatalogo {
  /** Declarado y todavía sin construir. Ver arriba. */
  unavailable?: true;
  /** Construido y ya no ofrecido. Ver arriba. */
  retirado?: true;
}

/**
 * Si el producto ofrece esto hoy. **El único predicado**: todas las listas de lo
 * ofrecido salen de acá.
 *
 * Existe porque las dos exclusiones se habían escrito por separado y una se
 * olvidó: `OFFERED_MODULES` filtraba `retirado` y no `unavailable`, así que
 * Ajustes › Módulos ofrecía el interruptor de «Automatizaciones» —un módulo que
 * no está construido— y el servidor contestaba 422 con un cartel rojo. Dos
 * filtros escritos a mano en dos lugares se separan; uno solo, no.
 */
export const seOfrece = (entrada: EstadoDeCatalogo): boolean =>
  entrada.unavailable !== true && entrada.retirado !== true;

export interface ModuleDefinition extends EstadoDeCatalogo {
  id: ModuleId;
  /** Sidebar label. */
  label: string;
  /** Route segment; the module owns everything under it. */
  path: string;
  /** lucide-react icon name, resolved lazily by the nav. */
  icon: string;
  /**
   * A qué nivel del producto pertenece: la decisión que ordena el menú, las
   * secciones de Ajustes › Módulos y la carpeta donde vive el código.
   *
   * - `base`: el producto. Agentes, Escalamiento Humano, Uso y Trazabilidad
   *   (decisión del dueño, 2026-09-14): lo que toda organización tiene, en ese
   *   orden, y el primer bloque del menú. Vive en `src/features/base/`.
   * - `extra`: todo lo construido antes de esa decisión —leads, contactos,
   *   empresas, negocios, bandeja…—. Sigue entero y funcionando; cada
   *   organización lo enciende según lo que contrató (LET su tablero, `ribox`
   *   lo que ya tenía). Vive en `src/features/extra/`.
   * - `admin`: la infraestructura de la cuenta —Configuración—, ni base ni extra.
   *
   * El nivel decide la sección de Ajustes, la carpeta y, por omisión, el bloque
   * del menú. `menuGroup` existe para las excepciones: el Resumen y Leads se
   * contratan como extra y se dibujan al tope del bloque base, en ese orden
   * (dueño, 2026-10-05 y 2026-10-19). Que la
   * carpeta coincida con el nivel lo exige `lib/niveles.test.ts`.
   */
  group: 'base' | 'extra' | 'admin';
  menuGroup?: 'base' | 'extra' | 'admin';
  /** Order within the group. */
  order: number;
  /** Modules that must also be enabled for this one to make sense. */
  requires?: ModuleId[];
  /**
   * El sistema sin el cual este módulo no se puede encender.
   *
   * Hermano de `requires` y de `unavailable`, y distinto de los dos.
   * `requires` nombra otro módulo, que se puede encender al lado;
   * `unavailable` dice que no existe para nadie. Esto dice que para **esta**
   * organización no hay servicio detrás: el interruptor se dibuja apagado y sin
   * poder moverse, y el servidor contesta 422 a quien lo mande igual.
   */
  requiresSystem?: SystemId;
  /** Capabilities this module can expose. */
  capabilities?: CapabilityId[];
  /** Shown in the entitlements admin UI. */
  description: string;
  // `unavailable` y `retirado` los hereda de `EstadoDeCatalogo`, que es donde
  // están dichos y argumentados para las cuatro listas del registro.
  //
  // Lo que es propio del MÓDULO y no cabe allá arriba: un módulo retirado
  // pierde la navegación —el menú, los atajos, el buscador y el interruptor de
  // Módulos— y **no** pierde su ruta, que redirige en vez de dar 404
  // (`app/routes.tsx`), porque un enlace viejo en un correo tiene que ir a parar
  // a algún lado. Y lo que ese módulo alimenta en otras pantallas sigue en pie:
  // el Resumen exige `dashboard` o `reports` para contestar, y el id sigue
  // escrito en la organización.
}

export const MODULES: Record<ModuleId, ModuleDefinition> = {
  dashboard: {
    id: 'dashboard',
    order: 0,
    label: 'Resumen',
    path: '/dashboard',
    icon: 'LayoutGrid',
    group: 'extra',
    menuGroup: 'base',
    description: 'Lo esencial del período: números grandes, gráficos y nada más.',
  },
  leads: {
    id: 'leads',
    label: 'Leads',
    path: '/leads',
    icon: 'Users',
    group: 'extra',
    menuGroup: 'base',
    order: 1,
    capabilities: ['leads.import', 'leads.export', 'leads.bulk_edit', 'leads.kanban'],
    // Sin «vista tabla»: se fue el 2026-09-08. La tabla de Leads era la lista de
    // Contactos con otro nombre, y la descripción es lo que un cliente lee al
    // decidir si contrata el módulo, así que prometía una pantalla que no está.
    description: 'Prospectos en el embudo: un tablero por etapas, y quién está en cada una.',
  },
  contacts: {
    id: 'contacts',
    label: 'Contactos',
    path: '/contacts',
    icon: 'Contact',
    group: 'extra',
    order: 2,
    // Las cinco llaves del modelo de personas se ofrecen ACÁ, y no en Leads,
    // porque el modelo ES la persona: `personas.create` da de alta a alguien y
    // `personas.edit` corrige sus datos, con o sin embudo detrás. Que Leads las
    // COMPRUEBE también no las hace suyas — de quién dependen para no ser peso
    // muerto lo dice `DUEÑO_DE_CAPACIDAD`, que nombra a los dos.
    //
    // Hasta hoy no las declaraba nadie, y ése era el hueco: sin módulo que las
    // nombrara no había tarjeta donde dibujar el interruptor, así que las cinco
    // no aparecían en Ajustes › Módulos y no había forma de encenderlas ni
    // apagarlas desde la pantalla.
    capabilities: [
      'contacts.merge',
      'personas.create',
      'personas.edit',
      'personas.assign',
      'personas.tag',
      'personas.stage',
      // El WhatsApp PERSONAL de la ficha, que no es el de la Bandeja. La tarjeta
      // es ésta porque la superficie es la ficha de la persona; la llave la
      // sostienen las dos pantallas de gente (`DUEÑO_DE_CAPACIDAD`), como sus
      // vecinas.
      'personas.whatsapp',
      // Editar el nombre y el teléfono. Apagada por omisión: ver
      // `CAPACIDADES_SIN_DEFECTO`.
      'personas.identity',
    ],
    description: 'Personas: datos, historial y relación con empresas.',
  },
  companies: {
    id: 'companies',
    label: 'Empresas',
    path: '/companies',
    icon: 'Building2',
    group: 'extra',
    order: 3,
    description: 'Cuentas: contactos asociados, negocios y actividad.',
  },
  deals: {
    id: 'deals',
    label: 'Negocios',
    path: '/deals',
    icon: 'Handshake',
    group: 'extra',
    order: 4,
    capabilities: ['deals.forecast'],
    description: 'Oportunidades por pipeline, con monto y proyección.',
  },
  activities: {
    id: 'activities',
    label: 'Actividad',
    path: '/activities',
    icon: 'Activity',
    group: 'extra',
    order: 5,
    description: 'Línea de tiempo unificada: notas, llamadas, correos, cambios.',
  },
  tasks: {
    id: 'tasks',
    label: 'Tareas',
    path: '/tasks',
    icon: 'CircleCheck',
    group: 'extra',
    order: 6,
    // Sin «y recordatorio»: no existen. `Task` lleva fecha de vencimiento y
    // nada que avise, y no hay quien lo mande (ver `features/extra/tasks/
    // backend-pending.ts` §4). La descripción es lo que un cliente lee al
    // decidir si contrata el módulo, así que prometía algo que no iba a
    // encontrar.
    description: 'Pendientes con vencimiento y responsable.',
    // Decisión del dueño, 2026-09-14: Tareas y Tickets dejan de existir como
    // módulos; los dos se convierten en Escalamiento Humano (`escalation`),
    // que es donde una persona toma lo que un agente no pudo cerrar y lo que
    // hay que cargar a mano. El código y los datos se quedan; la superficie no.
    retirado: true,
  },
  inbox: {
    id: 'inbox',
    label: 'Bandeja',
    path: '/inbox',
    icon: 'Inbox',
    group: 'extra',
    order: 7,
    capabilities: ['inbox.send'],
    requiresSystem: 'whatsapp',
    description: 'Conversaciones de los agentes de texto (WhatsApp y web).',
  },
  mail: {
    id: 'mail',
    label: 'Correo',
    path: '/mail',
    icon: 'Mail',
    group: 'extra',
    order: 7.5,
    capabilities: ['mail.tracking'],
    description:
      'Los correos de todas las casillas conectadas, con lo que el CRM no reconoce aparte.',
  },
  calls: {
    id: 'calls',
    label: 'Llamadas',
    path: '/calls',
    icon: 'PhoneCall',
    group: 'extra',
    order: 8,
    capabilities: ['calls.trigger'],
    requiresSystem: 'phone',
    description: 'Registro de llamadas del agente de voz, con grabación.',
  },
  calendar: {
    id: 'calendar',
    label: 'Calendario',
    path: '/calendar',
    icon: 'Calendar',
    group: 'extra',
    // Después de Llamadas, y `8.5` como `mail` usa `7.5`: el orden del menú es
    // un lugar entre dos vecinos, no un número entero que obligue a correr todo
    // lo que viene después cada vez que aparece un módulo en el medio.
    order: 8.5,
    // Dos capacidades, y ninguna es «lo que el módulo hace»: hasta el 2026-09-25
    // no tenía ninguna a propósito —tener el Calendario ES la decisión, y el
    // interruptor «Grabar» de cada reunión no es una llave aparte—. Ésta es de
    // otra clase: no decide qué pantalla se dibuja sino **qué se guarda de la
    // gente que estuvo en la sala**. El video no se muestra en ningún lado del
    // CRM, así que su interruptor no enciende ni apaga superficie alguna; se
    // dibuja acá porque Ajustes › Módulos es `platformOnly` y ése es el único
    // lugar donde la decisión es nuestra y no del cliente. La segunda
    // (2026-11-02) es de la misma clase: suma al bot a la invitación de las
    // reuniones de los clientes, o sea que escribe en su calendario.
    capabilities: ['calendar.recordVideo', 'calendar.inviteBot'],
    //
    // Tampoco `requiresSystem`: un calendario no lo sirve ni la telefonía ni
    // WhatsApp. Lo conecta cada persona con su cuenta, en Ajustes ›
    // Integraciones, y sin ninguna conexión la pantalla lo dice y enlaza ahí.
    description: 'Las reuniones de la organización, con su grabación.',
  },
  reports: {
    id: 'reports',
    label: 'Reportes',
    path: '/reports',
    icon: 'ChartColumn',
    group: 'extra',
    order: 11,
    capabilities: ['reports.custom'],
    description: 'Conversión, velocidad de embudo y desempeño por vendedor.',
    // Decisión del dueño, 2026-08-27: «deshabilita la pestaña de reportes, sólo
    // vamos a tener el resumen». Una sola pantalla de lectura, y es el Resumen.
    // El código de Reportes se queda entero donde está —nadie lo borró y el día
    // que vuelva es esta línea de menos—, pero el producto ya no lo ofrece.
    retirado: true,
  },
  tickets: {
    id: 'tickets',
    label: 'Tickets',
    path: '/tickets',
    icon: 'LifeBuoy',
    group: 'extra',
    order: 9,
    // Sin capacidad propia: tener la mesa de ayuda ES la decisión. Los permisos
    // finos —quién puede cerrar, quién sólo responde— son trabajo posterior y
    // no una llave que hoy no comprueba nadie.
    description: 'Mesa de ayuda: la conversación, sus datos y en qué quedó.',
    // Ver `tasks`: retirado el 2026-09-14, se convierte en Escalamiento Humano.
    retirado: true,
  },
  agents: {
    id: 'agents',
    label: 'Agentes',
    path: '/agents',
    icon: 'Bot',
    // El primero de la base: Ribox vende agentes (dueño, 2026-09-14). Se
    // PROMOVIÓ el módulo que ya existía en vez de crear otro: la vitrina ya es
    // «todos los agentes que tiene el cliente», no importa nada de otro módulo, y
    // seis organizaciones la tienen encendida.
    group: 'base',
    order: 2,
    // La vitrina es de sólo lectura y no necesita llave; MOVER el interruptor de
    // un agente sí, y es la única escritura del módulo que no la tenía. Apagada
    // por defecto: qué agente corre y qué agente no es nuestro.
    capabilities: ['agents.manage'],
    description: 'Los agentes de IA que trabajan para la organización.',
  },
  // Los otros tres de la base NACIERON VACÍOS a propósito (dueño, 2026-09-14):
  // título y página, para que cada uno se construya encima. Como todo lo demás,
  // se encienden por organización (ver `BASE_MODULE_IDS`: la base dice dónde van
  // en el menú, no que estén siempre). Las descripciones dicen para qué son, en
  // palabras del dueño, sin prometer pantallas que no están.
  escalation: {
    id: 'escalation',
    label: 'Escalamiento Humano',
    path: '/escalation',
    icon: 'Headset',
    group: 'base',
    order: 3,
    description:
      'Lo que necesita a una persona: problemas de soporte y datos que hay que cargar a mano.',
  },
  usage: {
    id: 'usage',
    label: 'Uso',
    path: '/usage',
    icon: 'Gauge',
    group: 'base',
    order: 4,
    description: 'Cuánto se está usando: la actividad y el consumo de la organización.',
  },
  traceability: {
    id: 'traceability',
    label: 'Trazabilidad',
    path: '/traceability',
    icon: 'ScrollText',
    group: 'base',
    order: 5,
    // **LA DEPENDENCIA, DECLARADA.** Trazabilidad no es una pantalla propia: es
    // la lectura de lo que hicieron los agentes. Sus tres endpoints resuelven a
    // qué operador preguntarle desde `agentes[].operador` —la misma tarjeta que
    // dibuja la vitrina— y la pantalla pide la lista de agentes antes de pedir
    // una sola corrida. Sin Agentes no hay nada que trazar, y encenderla sola
    // dejaría una pantalla que abre y contesta 403 en todo lo que pide.
    //
    // Con esto declarado, `nextModules` arrastra Agentes al encenderla y se
    // lleva Trazabilidad al apagar Agentes, y `resolveEnabledModules` la deja
    // fuera del menú si un documento editado a mano trajera la combinación
    // imposible. Del otro lado, el servidor exige `traceability` y nada más:
    // ver el encabezado de `backend/api/v2/agentes_corridas.py`.
    requires: ['agents'],
    // Sin capacidad propia, como la vitrina: es de sólo lectura y tener la
    // pantalla ES la decisión.
    description: 'El registro de lo que hicieron los agentes: cada corrida, sus pasos, sus llamadas y su resultado.',
  },
  bases: {
    id: 'bases',
    label: 'Bases',
    path: '/bases',
    icon: 'FileSpreadsheet',
    group: 'base',
    order: 6,
    description: 'Las bases que recorre el operador.',
  },
  automations: {
    id: 'automations',
    label: 'Automatizaciones',
    path: '/automations',
    icon: 'Workflow',
    group: 'extra',
    order: 12,
    capabilities: ['automations.run', 'automations.reply'],
    description: 'Reglas de etapa: mover o responder solas cuando pasa algo en un embudo.',
  },
  settings: {
    id: 'settings',
    label: 'Configuración',
    path: '/settings',
    icon: 'Settings',
    group: 'admin',
    order: 0,
    capabilities: [
      'settings.custom_fields',
      'settings.audit_log',
      'settings.integrations',
    ],
    description: 'Organización, usuarios, embudos, campos y accesos.',
  },
};

export const MODULE_LIST: ModuleDefinition[] = MODULE_IDS.map((id) => MODULES[id]);

/**
 * Los módulos BASE, en el orden del menú: el primer grupo, y en este orden.
 * Decisión del dueño, 2026-09-14: «they should be always there, at the top, in
 * the order I gave you».
 *
 * **«Siempre» se cayó el 2026-09-24 y «arriba» se quedó.** Hasta ese día estos
 * cuatro entraban solos: `resolveEnabledModules` los sumaba a lo que la
 * organización tuviera, `LOCKED_MODULES` no dejaba apagarlos y `hiddenNavModules`
 * no los alcanzaba. Ahora se contratan y se apagan como cualquier módulo, porque
 * una vista sin interruptor no se puede esconder —«we need to add toggles to any
 * new view so we can disable them»— y toda superficie nueva de la base llegaba
 * encendida a todos los clientes.
 *
 * Lo que la lista sigue diciendo es lo que nunca dependió del cliente: dónde van
 * en el menú (`GROUP_RANK`), y que su código vive en `src/features/base/`
 * (`lib/niveles.test.ts`). El espejo del servidor es `BASE_MODULE_IDS` en
 * `tenant_mapping.py`, con su prueba de deriva.
 */
export const BASE_MODULE_IDS: readonly ModuleId[] = MODULE_LIST.filter(
  (module) => module.group === 'base',
)
  .sort((a, b) => a.order - b.order)
  .map((module) => module.id);

/**
 * Los módulos que el producto OFRECE hoy: ni los no construidos ni los
 * retirados.
 *
 * Lo que mira la pantalla de Módulos, para no ofrecer un interruptor que no
 * cambia nada. `MODULE_LIST` sigue siendo la lista completa —las rutas la
 * recorren entera, y ahí está justamente lo que hace que la ruta de un módulo
 * retirado redirija en vez de dar 404—.
 *
 * Sale de `seOfrece` y no de un filtro propio: escrito a mano decía sólo
 * `retirado !== true`, y por eso Ajustes ofrecía «Automatizaciones» —que no
 * está construida— hasta que el servidor contestaba 422.
 */
export const OFFERED_MODULES: ModuleDefinition[] = MODULE_LIST.filter(seOfrece);

/**
 * De qué módulo depende cada capacidad. **Exhaustivo a propósito.**
 *
 * Una capacidad es peso muerto mientras el módulo que la sostiene está apagado:
 * `can('leads.import')` no puede contestar que sí sin la pantalla de Leads. Eso
 * ya se leía —del array `capabilities` de cada módulo, recorrido al revés— y ahí
 * estaba el hueco: **lo que ningún módulo nombraba no tenía dueño**, y las cinco
 * llaves de `personas.*` cayeron justo ahí. Sin dueño no hay veto.
 *
 * Es un `Record` y no un `Partial` para que el compilador exija el dueño de cada
 * llave nueva. Olvidarse es el defecto que no se ve: la llave funciona igual
 * hasta el día que alguien apaga el módulo, y ahí contesta que sí sobre una
 * pantalla que no existe.
 *
 * **LISTA Y NO UN SOLO MÓDULO** porque `personas.*` es de las DOS pantallas de
 * gente. Leads con `leadsSource: 'personas'` y Contactos con
 * `contactsSource: 'personas'` leen la misma colección y comprueban las mismas
 * llaves (`features/extra/{leads,contacts}/lib/source.ts`). Colgarlas de Contactos a
 * secas le apagaría el alta y la edición a una organización de sólo Leads, que
 * es una combinación que el producto soporta y que la suite prueba. Alcanza con
 * que UNO de los dueños esté encendido: si queda una sola de las dos pantallas,
 * la llave sigue queriendo decir algo ahí.
 */
export const DUEÑO_DE_CAPACIDAD: Record<CapabilityId, readonly ModuleId[]> = {
  'leads.import': ['leads'],
  'leads.export': ['leads'],
  'leads.bulk_edit': ['leads'],
  'leads.kanban': ['leads'],
  'deals.forecast': ['deals'],
  'contacts.merge': ['contacts'],
  'automations.run': ['automations'],
  'automations.reply': ['automations'],
  'reports.custom': ['reports'],
  'settings.custom_fields': ['settings'],
  'settings.audit_log': ['settings'],
  'settings.integrations': ['settings'],
  'inbox.send': ['inbox'],
  'mail.tracking': ['mail'],
  'calls.trigger': ['calls'],
  'calendar.recordVideo': ['calendar'],
  'calendar.inviteBot': ['calendar'],
  'agents.manage': ['agents'],
  'personas.create': ['contacts', 'leads'],
  'personas.edit': ['contacts', 'leads'],
  'personas.assign': ['contacts', 'leads'],
  'personas.tag': ['contacts', 'leads'],
  'personas.stage': ['contacts', 'leads'],
  'personas.whatsapp': ['contacts', 'leads'],
  'personas.identity': ['contacts', 'leads'],
};

/**
 * Las capacidades que NO se encienden solas. Espejo de `CAPACIDADES_SIN_DEFECTO`
 * en `tenant_mapping.py`, con una prueba que falla si divergen.
 *
 * Una organización que nunca declaró sus capacidades tiene TODAS las que sus
 * módulos sostienen: esconder por accidente algo que el cliente compró es el
 * error que nadie reporta. Éstas van al revés, y el motivo es de otra clase:
 * `mail.tracking` mide a alguien que no es cliente nuestro y no aceptó nada, así
 * que encenderla tiene que ser una decisión escrita y no el defecto de un
 * documento a medio llenar.
 *
 * Es sólo el DEFECTO: declarada, se enciende como cualquier otra.
 *
 * `agents.manage` se sumó el 2026-09-24 y es de una tercera clase: no es
 * peligrosa para un tercero, es **nuestra**. Pausar un agente es decidir qué
 * corre y qué no; se le puede dar a una organización, y no puede llegarle por el
 * defecto de un documento a medio llenar.
 *
 * `personas.whatsapp` es la más literal: el dueño dijo que no queremos permitir
 * conexiones de WhatsApp personales, y la superficie ya estaba construida y
 * encendida para toda organización con gente. Apagarla por defecto es lo que
 * hace que «no permitir» sea el estado y no un trámite.
 *
 * `calendar.recordVideo` es de la primera clase, la de `mail.tracking`, y más
 * cara: lo que se guardaría además de las voces son las caras y las pantallas
 * que alguien comparta, y el único aviso que recibe la sala sigue siendo un
 * participante mudo llamado «Ribox Notas».
 *
 * `calendar.inviteBot` (2026-11-02) es de la primera clase también: el CRM
 * escribe en el calendario de un tercero —el cliente ve a `notas@ribox.cl` entre
 * sus invitados— y le pide a cada persona el permiso de cambiar sus eventos.
 *
 * `personas.identity` (2026-10-04) es de una cuarta clase: peligrosa para los
 * datos de la organización que la reciba sin haberla pedido. Donde hay un espejo
 * del operario, editar el nombre o el teléfono de una persona se deshace solo en
 * la corrida siguiente. La identidad sigue sin editarse en toda organización
 * que no la tenga escrita.
 */
export const CAPACIDADES_SIN_DEFECTO: readonly CapabilityId[] = [
  'mail.tracking',
  'agents.manage',
  'personas.whatsapp',
  'calendar.recordVideo',
  'calendar.inviteBot',
  'personas.identity',
  'automations.reply',
];

/**
 * Las llaves que **ninguna pantalla comprueba**, porque quien las comprueba es
 * el servidor y lo que gobiernan no se dibuja en ninguna parte.
 *
 * Es la tercera excepción de `registro-vivo.test.ts`, hermana de `unavailable`
 * y de `CAMPOS_SIN_CONSTRUIR`, y vive acá por la misma razón que ellas: una
 * lista de excepciones escrita dentro de la prueba se vuelve un cajón donde se
 * mete un id para poner la suite en verde. Acá, agregar una es tomar la
 * decisión de producto de que esa llave no tiene ni va a tener pantalla.
 *
 * Y va en los dos sentidos: el día que alguien cablee una de éstas en el
 * navegador, la prueba se pone roja hasta que la saquen de la lista.
 *
 * `calendar.recordVideo` decide que el bot de la reunión grabe video además del
 * audio. El archivo es nuestro y no lo devuelve ninguna ruta del CRM —en la
 * ficha se oye el audio, igual que sin esto—, así que no hay ningún control que
 * dibujar ni esconder. Lo único que se ve es su interruptor en Ajustes ›
 * Módulos, que es una DECLARACIÓN (`MODULES.calendar.capabilities`) y no una
 * comprobación, y que sólo vemos nosotros (`platformOnly`).
 */
export const CAPACIDADES_SOLO_DEL_SERVIDOR: readonly CapabilityId[] = [
  'calendar.recordVideo',
  'agents.manage',
  'automations.run',
  'automations.reply',
];

/** Las capacidades que el producto ofrece hoy, sin las de un módulo que no ofrece. */
export const OFFERED_CAPABILITY_IDS: readonly CapabilityId[] = CAPABILITY_IDS.filter((id) =>
  DUEÑO_DE_CAPACIDAD[id].some((modulo) => seOfrece(MODULES[modulo])),
);

/**
 * Las pestañas de la ficha del lead.
 *
 * Una pestaña **no** es un módulo: es una de las superficies donde un módulo se
 * asoma. Bandeja tiene dos —su pantalla propia en el menú y la pestaña
 * «Conversación» de la ficha— y hasta ahora las dos colgaban del mismo
 * interruptor. Apagarle Bandeja a un cliente para sacarle la pantalla del menú
 * le quitaba de paso el registro de WhatsApp del lead, que es justo lo que sí
 * quería ver; lo mismo con Actividad y su línea de tiempo.
 *
 * Así que la superficie se declara aparte del módulo. El módulo sigue diciendo
 * qué **tiene** la organización —es lo que el servidor exige y sin él no hay
 * datos—, y estas listas dicen **dónde se ve** lo que tiene. Las dos superficies
 * son independientes entre sí: esconder la pantalla de Bandeja no toca la
 * pestaña, y esconder la pestaña no toca la pantalla.
 */
export const LEAD_TAB_IDS = [
  'resumen',
  'llamadas',
  'conversacion',
  'actividad',
  'tareas',
  'negocios',
  'archivos',
] as const;

export type LeadTabId = (typeof LEAD_TAB_IDS)[number];

/**
 * Los campos de un lead que una organización puede declarar que existen.
 *
 * Mismo vocabulario que el catálogo de la pantalla de Leads
 * (`features/extra/leads/lib/lead-fields.ts`), escrito acá porque es configuración:
 * lo lee el esquema del tenant, lo escribe alguien en la base, y el servidor
 * tiene su propia copia con una prueba que falla si las dos se separan.
 */
export const LEAD_FIELD_IDS = [
  'name',
  'company',
  'email',
  'phone',
  'stage',
  'status',
  'source',
  'owner',
  'lastActivity',
  'nextFollowUp',
  'tags',
  'created',
  // Los cuatro campos del CASO (decisión 23 del plan del modelo único). Entran
  // acá y en `backend/api/v2/tenant_mapping.py` en el MISMO cambio: hay una
  // prueba que falla si se separan, y en una lista aditiva un id que un lado no
  // reconoce esconde el campo de la pantalla sin que nadie lo haya pedido.
  'valor',
  'referenciaExterna',
  'estadoCaso',
  'seguimientoCaso',
  'ultimoEvento',
  'oi',
] as const;

export type LeadFieldId = (typeof LEAD_FIELD_IDS)[number];

/**
 * Qué campo de un lead depende de qué otra cosa. **Exhaustivo a propósito.**
 *
 * EL CRUCE DE EJES, escrito. Son tres —módulo, capacidad, campo— y hasta hoy no
 * había ningún lugar donde se cruzaran: cada superficie los combinaba a mano y
 * la que se olvidaba de uno dejaba el hueco. `company` es el caso de manual:
 * varias superficies preguntaban sólo por el campo y dibujaban una columna
 * «Empresa», una fila de ficha, un subtítulo y un selector de alta apuntando a
 * un módulo que la organización no tiene. Otras preguntaban por las dos cosas, y
 * una tercera dibujaba el control APAGADO con un cartel que explica lo que la
 * organización no compró — publicidad puesta adelante del trabajo.
 *
 * Con el dueño acá, apagar Empresas **descuenta el campo** y las superficies se
 * caen solas por el camino que ya existía —`visibleColumns`, `drawerRowVisible`,
 * `createFieldVisible`, todas leyendo el mismo `useLeadFields()`—. Ninguna tiene
 * que acordarse de nada, que es lo único que hace que no se olviden.
 *
 * SÓLO SE DECLARA LO QUE ES CIERTO. Un dueño de más esconde un campo que la
 * organización sí tiene, y ése es el error que nadie reporta —nadie extraña un
 * campo que no sabe que existe—:
 *
 * - `stage` NO cuelga de `leads.kanban`. Esa llave es el veto de la VISTA de
 *   tablero (`features/extra/leads/hooks/use-leads-screen.ts`, y lo mismo del lado del
 *   servidor en `backend/api/v2/access.py`), no la dueña de la etapa: una
 *   organización sin tablero sigue teniendo embudo, columna «Etapa» en la tabla,
 *   filtro, selector en la ficha y cierre del negocio. Colgar el campo de la
 *   llave le borraría las cinco cosas a quien sólo dijo que no quería arrastrar
 *   tarjetas. La relación existe y es al revés: el TABLERO necesita el campo, y
 *   eso se dice como requisito de esa superficie, no como dueño de este campo.
 * - `valor` NO cuelga de `deals`. Es el valor del CASO y lo dibujan también
 *   Leads y Velocidad, sin el módulo de Negocios de por medio.
 * - `lastActivity` NO cuelga de `activities`. Sale del propio registro y no de
 *   la línea de tiempo, así que una organización sin ese módulo lo sigue viendo.
 */
export interface DueñoDeCampo {
  /** El módulo sin el cual el campo no apunta a ninguna parte. */
  modulo?: ModuleId;
  /** Y la capacidad, cuando además hiciera falta una. Hoy ninguna la usa. */
  capacidad?: CapabilityId;
}

export const DUEÑO_DE_CAMPO: Record<LeadFieldId, DueñoDeCampo> = {
  name: {},
  // El único con dueño hoy, y el que motivó el mapa: el campo dice a qué cuenta
  // pertenece la persona, y sin el módulo de Empresas no hay cuenta a la que
  // apuntar ni pantalla donde abrirla.
  company: { modulo: 'companies' },
  email: {},
  phone: {},
  stage: {},
  status: {},
  source: {},
  owner: {},
  lastActivity: {},
  nextFollowUp: {},
  tags: {},
  created: {},
  valor: {},
  referenciaExterna: {},
  estadoCaso: {},
  seguimientoCaso: {},
  ultimoEvento: {},
  oi: {},
};

/**
 * Campos declarados que **ninguna pantalla lee todavía**.
 *
 * El tercer estado de `EstadoDeCatalogo` aplicado a los campos: están escritos
 * —entraron con `valor` en el mismo cambio, decisión 23 del plan del modelo
 * único— y no hay una sola línea que los gatee. Ofrecerlos igual es un
 * interruptor que no mueve nada, que es exactamente el defecto que este archivo
 * arregla en los módulos.
 *
 * `estadoCaso` además CHOCA con `status`: son dos ids para el mismo dato, y el
 * que la pantalla lee es `status` (`features/extra/leads/components/detail/
 * summary-tab.tsx`). Encenderlos sin resolver eso deja dos interruptores para
 * una sola columna, y quien mueva el que no es no ve pasar nada.
 *
 * **NO SE BORRAN LOS IDS.** El servidor tiene su copia de `LEAD_FIELD_IDS` con
 * una prueba que compara las dos tuplas enteras, y una organización puede tener
 * el id ya escrito en `enabledLeadFields`. Retirar no es borrar.
 *
 * Es una LISTA y no un `unavailable: true` dentro de `DUEÑO_DE_CAMPO` por una
 * razón fea que conviene decir en voz alta: `tests/api/test_v2_tenants.py` busca
 * el texto `unavailable: true` en ESTE archivo y da por hecho que cada aparición
 * es un módulo. Una segunda aparición, aunque sea de un campo, rompe esa prueba
 * con un mensaje que no explica nada. Vale la pena anclar ese escaneo al bloque
 * `MODULES`; mientras no lo esté, esta lista dice lo mismo sin pisarlo.
 */
export const CAMPOS_SIN_CONSTRUIR: readonly LeadFieldId[] = [
  'referenciaExterna',
  'estadoCaso',
  'seguimientoCaso',
];

/**
 * De dónde sale la lista de Contactos de una organización.
 *
 * `legacy` es la proyección de siempre —los registros de los embudos— y es el
 * valor por defecto. `personas` lee la colección persona-céntrica, que es donde
 * el espejo del operario deja gente que **no tiene fila en ningún embudo** y
 * que hoy, por eso, no aparece en ninguna pantalla.
 *
 * Es una elección por organización y no una mudanza: una necesita el modelo
 * nuevo ahora y otra depende de columnas y filtros que el modelo nuevo todavía
 * no tiene. El servidor tiene su copia de esta lista, con una prueba que falla
 * si las dos se separan.
 */
export const CONTACTS_SOURCES = [
  'legacy',
  'personas',
] as const;

export type ContactsSource = (typeof CONTACTS_SOURCES)[number];

export const DEFAULT_CONTACTS_SOURCE: ContactsSource = 'legacy';

/**
 * La fuente de una organización, con lo irreconocible cayendo a la de siempre.
 *
 * Ausente, nulo, una palabra que no conocemos o un despliegue del frontend
 * anterior al del servidor: todos terminan en `legacy`. Equivocarse hacia ahí
 * deja a alguien viendo lo que veía ayer —molesto y visible al instante—;
 * equivocarse hacia `personas` le cambia la pantalla entera a quien no lo pidió.
 */
export const resolveContactsSource = (value: unknown): ContactsSource =>
  (CONTACTS_SOURCES as readonly string[]).includes(value as string)
    ? (value as ContactsSource)
    : DEFAULT_CONTACTS_SOURCE;

/**
 * De dónde sale la lista de Leads.
 *
 * Misma decisión que la de Contactos y por las mismas razones, sobre la otra
 * pantalla: para el dueño **todo lead es un contacto**, así que una organización
 * en el modelo de personas ve la misma gente en las dos.
 */
export const LEADS_SOURCES = [
  'legacy',
  'personas',
] as const;

export type LeadsSource = (typeof LEADS_SOURCES)[number];

export const DEFAULT_LEADS_SOURCE: LeadsSource = 'legacy';

/**
 * De dónde sale la lista de Llamadas.
 *
 * `legacy` son los eventos `call` de la línea de tiempo de cada embudo, leídos
 * como llamadas con grabación y transcripción. `eventos` es la colección de
 * eventos de personas, donde una llamada es un hecho más y lo que se muestra de
 * ella lo decide quien la escribió.
 */
export const CALLS_SOURCES = [
  'legacy',
  'eventos',
] as const;

export type CallsSource = (typeof CALLS_SOURCES)[number];

export const DEFAULT_CALLS_SOURCE: CallsSource = 'legacy';

/**
 * La misma regla para las tres llaves: lo irreconocible cae a la de siempre.
 *
 * Ausente, nulo, una palabra que no conocemos o un despliegue del frontend
 * anterior al del servidor. Y la palabra de OTRA llave tampoco sirve: `eventos`
 * no es una fuente de Leads ni `personas` una de Llamadas — son dos colecciones
 * distintas, y confundirlas dejaría la pantalla pidiendo algo que no existe.
 */
const resolveSource = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  (allowed as readonly string[]).includes(value as string) ? (value as T) : fallback;

export const resolveLeadsSource = (value: unknown): LeadsSource =>
  resolveSource(value, LEADS_SOURCES, DEFAULT_LEADS_SOURCE);

export const resolveCallsSource = (value: unknown): CallsSource =>
  resolveSource(value, CALLS_SOURCES, DEFAULT_CALLS_SOURCE);

/**
 * Las secciones que el Resumen sabe construir.
 *
 * **EL SERVIDOR DECIDE QUÉ SECCIONES EXISTEN**, y esta lista no lo contradice:
 * es el vocabulario con el que la configuración puede *nombrarlas* para
 * esconder alguna. El servidor tiene su copia en
 * `backend/api/v2/tenant_mapping.py`, con una prueba que falla si las dos se
 * separan — un id renombrado en un lado deja el interruptor inerte, con el
 * `PATCH` contestando 422.
 *
 * El orden es el de la pantalla, y es del producto: qué entra, qué se marcó,
 * cómo avanza, qué se conversó. La configuración elige cuáles, nunca en qué
 * orden.
 */
export const RESUMEN_SECCION_IDS = ['cobranza', 'actividad', 'leads', 'llamadas', 'conversion', 'mensajes'] as const;

export type ResumenSeccionId = (typeof RESUMEN_SECCION_IDS)[number];

export interface ResumenSeccionDefinition {
  id: ResumenSeccionId;
  label: string;
  description: string;
  /** El módulo sin el cual el servidor no construye esta sección. */
  module: ModuleId;
  /** Y la capacidad, cuando además hace falta una. */
  capability?: CapabilityId;
}

/**
 * De qué depende cada sección, dicho del lado del cliente.
 *
 * Es la MITAD DE CONFIGURACIÓN de la compuerta del servidor —módulo, y para
 * Conversión también `leads.kanban` y el campo `stage`—, no la compuerta
 * entera: la otra mitad es el dato (que existan etapas), que sólo el servidor
 * ve. Por eso esto sirve para decidir qué interruptores OFRECER y nunca para
 * afirmar que una sección se va a dibujar.
 */
export const RESUMEN_SECCIONES: Record<ResumenSeccionId, ResumenSeccionDefinition> = {
  cobranza: {
    id: 'cobranza',
    label: 'Cobranza',
    description:
      'Qué lograron las llamadas de cobranza: contacto con el titular, compromisos de pago y cuándo prometieron pagar. Sólo aparece si la organización cobra con agentes.',
    module: 'dashboard',
  },
  actividad: {
    id: 'actividad',
    label: 'Actividad comercial',
    description:
      'Cuánto escribió, llamó y se reunió cada persona con gente de afuera. Sólo aparece si eliges a quiénes medir, más abajo.',
    // No cuelga de un módulo propio: la enciende la lista de personas a medir
    // (`resumenActividad`), y el Resumen es el único módulo que necesita.
    module: 'dashboard',
  },
  leads: {
    id: 'leads',
    label: 'Leads',
    description: 'Cuántas personas entran y cuántas quedan sin atención.',
    module: 'leads',
  },
  llamadas: {
    id: 'llamadas',
    label: 'Llamadas',
    description: 'Cuánto se marcó, a cuántos asuntos, y cómo terminó cada llamada.',
    module: 'calls',
  },
  conversion: {
    id: 'conversion',
    label: 'Conversión',
    description: 'Cuántos casos hay hoy parados en cada etapa y entre qué dos se cae más gente.',
    module: 'leads',
    capability: 'leads.kanban',
  },
  mensajes: {
    id: 'mensajes',
    label: 'Mensajes',
    description: 'Cuánto se conversa por escrito y qué quedó esperando respuesta.',
    module: 'inbox',
  },
};

export const RESUMEN_SECCION_LIST: ResumenSeccionDefinition[] = RESUMEN_SECCION_IDS.map(
  (id) => RESUMEN_SECCIONES[id],
);

/**
 * Si la configuración de esta organización PODRÍA hacer aparecer esta sección.
 *
 * Deliberadamente optimista: contesta que sí siempre que los módulos y las
 * capacidades lo permitan, aunque el servidor después no dibuje la sección
 * porque no hay etapas o no hay datos. La alternativa —esconder el interruptor
 * de una sección que el servidor sí construye— dejaría una sección imposible de
 * apagar desde la pantalla, que es peor que ofrecer un interruptor de más.
 */
export function resumenSeccionPosible(
  id: ResumenSeccionId,
  modules: ReadonlySet<ModuleId>,
  capabilities: ReadonlySet<CapabilityId>,
  leadFields: readonly LeadFieldId[] | null | undefined,
): boolean {
  const seccion = RESUMEN_SECCIONES[id];
  if (!modules.has(seccion.module)) return false;
  if (seccion.capability !== undefined && !capabilities.has(seccion.capability)) return false;
  // La misma asimetría que en todas partes: la lista ausente son TODOS los
  // campos, y sólo una lista escrita que no incluya `stage` apaga el embudo.
  if (id === 'conversion' && leadFields != null && !leadFields.includes('stage')) return false;
  return true;
}

/**
 * El campo que no se puede apagar: sin nombre, una fila no es nadie.
 *
 * Una tabla de leads sin la columna del nombre no es una tabla más angosta, es
 * una lista de filas anónimas donde no se puede ni elegir cuál abrir.
 */
export const ALWAYS_ON_LEAD_FIELDS: readonly LeadFieldId[] = ['name'];

/**
 * Qué campos existen para esta organización. **Aditivo: mínimo primero.**
 *
 * - Sin la clave escrita (`null`/`undefined`) **están todos**. Es el caso de
 *   toda organización que nadie configuró, y tiene que verse exactamente igual
 *   que antes de que este mecanismo existiera.
 * - Con la clave escrita existen **sólo los enumerados**, más el nombre.
 *
 * Va al revés que `hiddenLeadTabs`, y el dueño lo decidió así el 2026-08-21:
 * una organización empieza con lo mínimo y pide lo que necesita. La
 * consecuencia hay que decirla porque es real y es el costo del cambio de
 * polaridad: un campo NUEVO no le va a aparecer a quien tenga la lista escrita
 * hasta que alguien lo agregue a esa lista. Con una lista de exclusiones pasaba
 * lo contrario —lo nuevo aparecía solo—; con ésta, aparecer es un acto
 * deliberado. Ninguna de las dos es gratis; ésta es la que se eligió.
 *
 * Es una función y no un `useMemo` para poder probar la regla sin montar la
 * aplicación, igual que `resolveEnabledModules`.
 */
export function resolveLeadFields(
  enabled: readonly LeadFieldId[] | null | undefined,
): Set<LeadFieldId> {
  // `null` y `undefined` son lo mismo acá a propósito: el segundo es lo que
  // manda una API desplegada antes que este campo existiera, y esconder por
  // accidente es el error que nadie reporta —nadie extraña un campo que no sabe
  // que existe—.
  if (enabled === null || enabled === undefined) return new Set(LEAD_FIELD_IDS);
  return new Set([...ALWAYS_ON_LEAD_FIELDS, ...enabled]);
}

export interface LeadTabDefinition extends EstadoDeCatalogo {
  id: LeadTabId;
  /** Rótulo de la pestaña; el mismo que dibuja la ficha. */
  label: string;
  /**
   * El módulo del que salen los datos.
   *
   * `undefined` significa que la pestaña se arma con la ficha misma y no le pide
   * nada a nadie. Con módulo, apagarlo deja la pestaña sin nada que mostrar, así
   * que desaparece — el interruptor de la superficie no puede resucitar datos
   * que el servidor no va a entregar.
   */
  module?: ModuleId;
  /** No se apaga: una ficha sin ninguna pestaña no es una ficha. */
  locked?: boolean;
  /** Se muestra en la pantalla que las enciende y apaga. */
  description: string;
}

export const LEAD_TABS: Record<LeadTabId, LeadTabDefinition> = {
  resumen: {
    id: 'resumen',
    label: 'Resumen',
    locked: true,
    description: 'Los datos del lead: contacto, empresa, etiquetas y campos propios.',
  },
  // LAS DOS RETIRADAS (dueño, 2026-09-02): las llamadas y el hilo de WhatsApp se
  // fundieron en la línea de tiempo de «Actividad», y desde entonces ninguna
  // ficha las dibuja — `lead-drawer.tsx` arma cuatro pestañas y ninguna es
  // éstas. Quedaron en la lista igual, y eso se veía: Ajustes › Vistas ofrecía
  // SEIS interruptores para una ficha de CUATRO pestañas, y el selector «Abre
  // en» decía «Automático (Conversación)» sobre una ficha que abre en Actividad.
  //
  // Retirarlas es una línea acá y llega a las dos pantallas por el camino que ya
  // existe (`leadTabVisible`), sin tocar ninguna. **Los ids NO se sacan de
  // `LEAD_TAB_IDS`**: el servidor tiene su copia, compara las tuplas enteras, y
  // toda organización que ya tenga `conversacion` escrito en `hiddenLeadTabs` o
  // en `leadTabInicial` necesita que el id siga siendo reconocible — si no, el
  // `PATCH` de su próxima edición contesta 422. Retirar no es borrar.
  llamadas: {
    id: 'llamadas',
    label: 'Llamadas',
    module: 'calls',
    retirado: true,
    description: 'Las llamadas de esta persona, con lo que se dijo y lo que se anotó.',
  },
  conversacion: {
    id: 'conversacion',
    label: 'Conversación',
    module: 'inbox',
    retirado: true,
    description: 'El hilo de WhatsApp de esta persona, con la opción de responderle.',
  },
  actividad: {
    id: 'actividad',
    label: 'Actividad',
    module: 'activities',
    description: 'Línea de tiempo del lead y el cuadro para escribir una nota.',
  },
  tareas: {
    id: 'tareas',
    label: 'Tareas',
    module: 'tasks',
    // Se va con su módulo (2026-09-14): la pestaña de escalamientos de la
    // persona la traerá Escalamiento Humano.
    retirado: true,
    description: 'Pendientes de este lead, con su vencimiento y responsable.',
  },
  negocios: {
    id: 'negocios',
    label: 'Negocios',
    module: 'deals',
    description: 'Negocios abiertos de la misma persona, para no duplicar una venta.',
  },
  archivos: {
    id: 'archivos',
    label: 'Archivos',
    description: 'Grabaciones de las llamadas y documentos que viajaron por correo con esta persona.',
  },
};

export const LEAD_TAB_LIST: LeadTabDefinition[] = LEAD_TAB_IDS.map((id) => LEAD_TABS[id]);

/**
 * Las pestañas que el producto OFRECE hoy: lo que la ficha puede llegar a
 * dibujar y, por lo tanto, lo único que Ajustes tiene sentido que ofrezca.
 *
 * Del mismo predicado que `OFFERED_MODULES`, para que no se separen.
 */
export const OFFERED_LEAD_TABS: LeadTabDefinition[] = LEAD_TAB_LIST.filter(seOfrece);

/**
 * Si la ficha del lead dibuja esta pestaña.
 *
 * Las cuatro reglas en el orden en que mandan:
 *
 * 1. Lo que el producto no ofrece —retirado o sin construir— no lo dibuja
 *    nadie, ni siquiera si está bloqueada. Es la decisión de más arriba y por
 *    eso va primero: no depende de esta organización.
 * 2. La pestaña bloqueada se ve siempre. Una ficha sin ninguna pestaña no es
 *    una ficha, y el interruptor que la dejara así no tendría vuelta.
 * 3. Sin el módulo que la alimenta, no. Esto no es una preferencia: el servidor
 *    contesta 403, así que encenderla igual dibujaría una pestaña que sólo
 *    puede fallar.
 * 4. Recién ahí, lo que hayamos escondido a mano.
 *
 * Vive acá y no dentro del contexto de React porque es la regla, no la
 * pantalla: así se puede probar sin montar nada, igual que
 * `resolveEnabledModules`.
 */
export function leadTabVisible(
  id: LeadTabId,
  modules: ReadonlySet<ModuleId>,
  hidden: ReadonlySet<LeadTabId>,
): boolean {
  const tab = LEAD_TABS[id];
  if (!seOfrece(tab)) return false;
  if (tab.locked) return true;
  if (tab.module !== undefined && !modules.has(tab.module)) return false;
  return !hidden.has(id);
}

const GROUP_RANK: Record<ModuleDefinition['group'], number> = { base: 0, extra: 1, admin: 2 };

export const grupoDelMenu = (module: ModuleDefinition): ModuleDefinition['group'] =>
  module.menuGroup ?? module.group;

/**
 * Lo que una superficie NECESITA, dicho en los ejes que existen.
 *
 * Una superficie es cualquier cosa que se dibuja y se puede no dibujar: una
 * columna, una fila de ficha, una pestaña, una faceta, un campo del alta, un
 * botón, una sección. Todas se gatean con la misma pregunta y hasta hoy cada una
 * la armaba a mano, así que la que se olvidaba de un eje dejaba el hueco.
 *
 * Los campos que se declaran se exigen TODOS (`y`, no `o`): pedir el módulo de
 * Empresas **y** el campo `company` es lo normal, porque son dos cosas
 * distintas —una es si hay dónde mirar, la otra si el dato existe— y las dos
 * tienen que ser ciertas para que valga la pena dibujar.
 *
 * Los que no se declaran no se preguntan: `{}` es «esto se ve siempre», que es
 * lo correcto para todo lo que no depende del plan.
 */
export interface Requisito {
  modulo?: ModuleId;
  capacidad?: CapabilityId;
  campo?: LeadFieldId;
  pestana?: LeadTabId;
}

/**
 * Cuál de los ejes faltó. **Nunca una frase.**
 *
 * Y ahí está la doctrina, escrita en el tipo. `docs/DECISIONES.md` §18 dice que
 * lo que la organización no contrató **no está** —ni deshabilitado ni con un
 * cartel: ausente— y el repo la incumplía en varios lugares, a veces en la misma
 * función. La causa no era descuido: era que los motivos de PRODUCTO y los del
 * REGISTRO viajaban por la misma cadena `disabledReason`, así que escribir el
 * cartel equivocado era el camino corto.
 *
 * Devolver el eje y no un texto hace que no haya nada que dibujar. Sirve para
 * decidir, para una prueba y para la pantalla de Ajustes —donde explicar por qué
 * no se puede encender algo SÍ corresponde, y para eso está `system-gates.ts`—,
 * y no sirve para ponerle un cartel a quien está trabajando.
 */
export type MotivoDeProducto =
  | { eje: 'modulo'; id: ModuleId }
  | { eje: 'capacidad'; id: CapabilityId }
  | { eje: 'campo'; id: LeadFieldId }
  | { eje: 'pestana'; id: LeadTabId };

/**
 * Una acción declarada con sus DOS motivos separados.
 *
 * La distinción que faltaba, y la razón de que `drawer-quick-actions.tsx`
 * argumentara en ocho líneas por qué «Agendar» desaparece y dos líneas antes
 * dejara «Llamar» apagado con un cartel que dice «Llamadas está apagado para tu
 * organización»:
 *
 * - **PRODUCTO** (módulo o capacidad apagados) → la acción NO SE RENDERIZA.
 *   Nunca un cartel. Un botón apagado dice «esto existe y podrías tenerlo», y en
 *   una organización que sólo llama por teléfono eso es publicidad puesta
 *   adelante del trabajo. El dueño lo confirmó, textual: «if deactivated, it
 *   shouldn't appear anywhere or make it seem as its missing».
 * - **REGISTRO** (sin teléfono, opt-out, `doNotContact`) → sí se renderiza,
 *   deshabilitada y CON su frase, porque ahí saber por qué es exactamente lo que
 *   la persona necesita: se arregla cargando el teléfono.
 *
 * El primero es un `Requisito` y no un `string` a propósito: así no hay ninguna
 * frase que dibujar aunque alguien quiera. La regla queda en el tipo y no en la
 * costumbre.
 */
export interface AccionDeclarada {
  motivoDeProducto?: Requisito;
  motivoDelRegistro?: string | null;
}

/** Qué hacer con la acción. Tres estados y ninguno ambiguo. */
export type EstadoDeAccion =
  | { estado: 'ausente' }
  | { estado: 'deshabilitada'; motivo: string }
  | { estado: 'disponible' };

/**
 * La acción resuelta contra lo que la organización tiene.
 *
 * Función suelta además de método de `ResolvedSurfaces` para lo que no está
 * dentro de un componente: una lista de acciones armada en un hook, una barra de
 * acciones en lote, la prueba de tabla que fija la regla.
 */
export function resolverAccion(
  accion: AccionDeclarada,
  muestra: (requisito: Requisito) => boolean,
): EstadoDeAccion {
  if (accion.motivoDeProducto !== undefined && !muestra(accion.motivoDeProducto)) {
    return { estado: 'ausente' };
  }
  // `trim()` porque un motivo vacío es la forma más silenciosa de dejar un botón
  // apagado sin explicación, que es justo el acertijo que esto viene a evitar.
  const registro = accion.motivoDelRegistro?.trim();
  return registro ? { estado: 'deshabilitada', motivo: registro } : { estado: 'disponible' };
}

/**
 * Las capacidades que valen, ya descontadas las de un módulo apagado.
 *
 * `null`/`undefined` son **todas menos `CAPACIDADES_SIN_DEFECTO`**, espejo de lo
 * que hace el servidor (`_CAPACIDADES_POR_DEFECTO` en `tenant_mapping.py`) y de
 * la asimetría de siempre: entre el despliegue del frontend y el de la API la
 * respuesta no trae el campo, y esconder por accidente es el error que nadie
 * reporta. La excepción es al revés porque su motivo es de otra clase: ver
 * `CAPACIDADES_SIN_DEFECTO`.
 *
 * El descuento es la mitad que faltaba: una llave sin su módulo es peso muerto,
 * y contestar que sí sobre una pantalla que no existe es cómo una acción termina
 * dibujada donde no hay nada que hacer con ella.
 */
export function resolveCapabilities(
  granted: readonly CapabilityId[] | null | undefined,
  modules: ReadonlySet<ModuleId>,
): Set<CapabilityId> {
  const declaradas =
    granted == null
      ? new Set<CapabilityId>(CAPABILITY_IDS.filter((id) => !CAPACIDADES_SIN_DEFECTO.includes(id)))
      : new Set(granted);
  const resueltas = new Set<CapabilityId>();
  for (const capacidad of declaradas) {
    // Una llave que este frontend no conoce —la API viene de un despliegue más
    // nuevo— no se puede ubicar, y adivinarle dueño sería inventar. Se ignora.
    const dueños = DUEÑO_DE_CAPACIDAD[capacidad];
    if (dueños === undefined) continue;
    if (dueños.some((modulo) => modules.has(modulo))) resueltas.add(capacidad);
  }
  return resueltas;
}

/**
 * Los campos que valen, ya descontados los que su dueño apagó.
 *
 * La otra mitad del cruce. `resolveLeadFields` contesta qué campos DECLARÓ la
 * organización; esto contesta cuáles quedan en pie una vez que se miran los
 * otros dos ejes — y es lo que hace que apagar Empresas borre la columna, la
 * fila de la ficha, el subtítulo y el selector del alta sin que ninguna de esas
 * cuatro superficies tenga que enterarse.
 *
 * Son dos funciones y no una porque son dos preguntas: lo DECLARADO es el dato
 * de la organización —lo que su `enabledLeadFields` dice, y lo que la pantalla
 * de campos tiene que seguir ofreciendo— y lo RESUELTO es lo que se dibuja.
 * Mezclarlas dejaría a Ajustes sin poder mostrar un campo cuyo módulo está
 * apagado, que es justo el interruptor que hay que poder mover para arreglarlo.
 *
 * Acá también caen los `CAMPOS_SIN_CONSTRUIR`: lo que no lee ninguna pantalla no
 * está para nadie, lo tenga o no escrito en su configuración.
 */
export function resolveCampos(
  enabled: readonly LeadFieldId[] | null | undefined,
  modules: ReadonlySet<ModuleId>,
  capabilities: ReadonlySet<CapabilityId>,
): Set<LeadFieldId> {
  const resueltos = new Set<LeadFieldId>();
  for (const campo of resolveLeadFields(enabled)) {
    if (CAMPOS_SIN_CONSTRUIR.includes(campo)) continue;
    const dueño = DUEÑO_DE_CAMPO[campo];
    if (dueño.modulo !== undefined && !modules.has(dueño.modulo)) continue;
    if (dueño.capacidad !== undefined && !capabilities.has(dueño.capacidad)) continue;
    resueltos.add(campo);
  }
  return resueltos;
}

/**
 * Por qué una organización **no puede tener** el tablero de Leads.
 *
 * Dos motivos y no uno, porque son dos decisiones distintas con dos textos
 * distintos: `capacidad` es una llave que alguien apagó y que se vuelve a
 * encender en Módulos; `campo` es que la organización no tiene `stage`, y eso no
 * se enciende desde ninguna pantalla porque `enabledLeadFields` lo escribimos
 * nosotros (docs/DECISIONES.md §8).
 *
 * La TERCERA causa —que no haya embudo, o que el embudo no tenga etapas— no
 * está acá a propósito: ésa no es configuración de producto sino configuración
 * incompleta, sólo se sabe después de pedir los embudos, y su respuesta es otra
 * (ver `features/extra/leads/leads-screen.tsx`). Fundir las tres en una bandera es lo
 * que dejaba a una organización con el embudo perfectamente armado leyendo
 * «todavía no tiene el embudo configurado».
 */
export type MotivoSinTablero = 'capacidad' | 'campo';

/**
 * Si esta organización puede tener tablero, y si no, por qué no. `null` = puede.
 *
 * La mitad de CONFIGURACIÓN de la compuerta del servidor —`hay_kanban` en
 * `backend/api/v2/access.py`, decisión 9 del plan del modelo único—, que es la
 * única mitad que se puede contestar sin pedir nada. La otra mitad es el dato
 * (que existan etapas) y la contesta la pantalla.
 *
 * SE PREGUNTA EN EL CASCARÓN, no dentro de Leads, y ésa es la corrección: sin
 * tablero **Leads no es una pantalla peor, no es ninguna** —era el interruptor
 * Tabla/Kanban el que le dejaba una salida, y ese interruptor se fue—. Un módulo
 * que no se puede usar no está: ni en el menú, ni en los atajos, ni en la
 * paleta, y su ruta contesta «sin acceso» (docs/DECISIONES.md §18).
 */
export function motivoSinTablero(
  capabilities: ReadonlySet<CapabilityId>,
  leadFields: ReadonlySet<LeadFieldId>,
  leadsSource: LeadsSource,
): MotivoSinTablero | null {
  if (!capabilities.has('leads.kanban')) return 'capacidad';
  // Con la fuente de personas, mover de etapa **es** el tablero: sus columnas
  // son las etapas y arrastrar una tarjeta es el único gesto que tiene. Sin
  // `personas.stage` el servidor rechaza cada movimiento, así que el tablero
  // quedaría de adorno. Ver `features/extra/leads/lib/source.ts`.
  if (leadsSource === 'personas' && !capabilities.has('personas.stage')) return 'capacidad';
  if (!leadFields.has('stage')) return 'campo';
  return null;
}

/**
 * Cuál de las dos llaves del tablero es la que falta.
 *
 * `motivoSinTablero` contesta el EJE —`capacidad` o `campo`— porque su
 * destinatario es la pantalla de «sin tablero», y ahí lo único que hace falta
 * saber es si se arregla en Módulos o escribiéndonos. `motivoDeProducto` en
 * cambio devuelve el **id de la raíz**, así que hay que nombrar cuál.
 *
 * No es una segunda copia de la regla: es leerla al derecho. Si el eje fue la
 * capacidad, la que falta es una de las dos que `motivoSinTablero` mira, y con
 * `leads.kanban` puesta la que queda es `personas.stage`. Si algún día esa regla
 * mira una tercera llave, esto es lo otro que hay que tocar — y son cinco líneas
 * juntas justamente para que se vea.
 */
function llaveDelTablero(capabilities: ReadonlySet<CapabilityId>): CapabilityId {
  return capabilities.has('leads.kanban') ? 'personas.stage' : 'leads.kanban';
}

export interface ResolvedSurfaces {
  /** Lo que la organización tiene, ya resueltas las dependencias. */
  modules: Set<ModuleId>;
  /** Sus llaves finas, ya descontadas las de un módulo apagado. */
  capabilities: Set<CapabilityId>;
  /** Sus campos, ya descontados los de un dueño apagado y los sin construir. */
  leadFields: Set<LeadFieldId>;
  /** Sus definiciones, en el orden en que se leen de arriba abajo. */
  enabledModules: ModuleDefinition[];
  /** Las que además el cascarón ofrece como destino. Subconjunto del anterior. */
  navModules: ModuleDefinition[];
  showsInNav: (id: ModuleId) => boolean;
  showsLeadTab: (id: LeadTabId) => boolean;
  /**
   * **LA PREGUNTA ÚNICA.** Si esta superficie se dibuja para esta organización.
   *
   * Sirve para una columna, una fila, una pestaña, una faceta, un campo del alta
   * y un botón, porque todas son la misma pregunta con distintos ejes puestos.
   */
  muestra: (requisito: Requisito) => boolean;
  /** Cuál eje faltó, para decidir y para probar. Nunca para dibujar un cartel. */
  motivoDeProducto: (requisito: Requisito) => MotivoDeProducto | null;
  /** `resolverAccion` con esta organización ya puesta. */
  accion: (accion: AccionDeclarada) => EstadoDeAccion;
  /**
   * Por qué Leads no se ofrece como destino, cuando no se ofrece.
   *
   * `null` es el caso normal. Con un motivo, `leads` ya salió de `navModules` y
   * de `showsInNav`; quien dibuja la ruta lo lee para decir cuál de los dos.
   *
   * Sobrevive al cruce de ejes —donde `muestra({ modulo: 'leads' })` ya contesta
   * lo mismo— porque contesta **otra cosa**: `muestra` decide si se dibuja y
   * `motivoDeProducto` nombra la raíz en el vocabulario del plan
   * (`leads.kanban`, `stage`), y la pantalla de «sin tablero» necesita el
   * vocabulario de la PERSONA: una llave que se enciende en Módulos o una
   * configuración que hacemos nosotros. Son dos textos distintos y esa
   * diferencia es el motivo entero de que `MotivoSinTablero` tenga dos valores.
   */
  sinTablero: MotivoSinTablero | null;
}

/**
 * De lo contratado y lo escondido a lo que se ve, en un solo lugar.
 *
 * **EL CRUCE DE LOS TRES EJES.** Hasta acá recibía módulos y las dos listas de
 * ocultamiento, y las capacidades y los campos se resolvían aparte, en
 * `entitlements.tsx`, sin que nadie los cruzara: por eso `can()` podía contestar
 * que sí sobre un módulo apagado y una columna podía dibujarse sobre un módulo
 * que no está. Ahora entran los seis datos y sale una sola respuesta.
 *
 * Las dos superficies de navegación salen de **listas distintas**
 * —`hiddenNavModules` y `hiddenLeadTabs`— y ése sigue siendo el punto: mientras
 * compartieron una, esconder la pantalla de Bandeja le borraba al lead su
 * conversación de WhatsApp, que es justo lo que el cliente sí quería ver.
 *
 * Los tres últimos parámetros son opcionales porque **ausente no es vacío**: es
 * una respuesta de una API desplegada antes que el campo, y significa «todo».
 * Quien llama con tres argumentos —varias pruebas lo hacen— sigue describiendo
 * una organización sin recortes, que es lo que describía antes.
 *
 * **Y de ahí sale, sin pedir nada aparte, si Leads puede tener tablero.** Eso
 * entraba por un objeto propio (`ConfiguracionDeLeads`) cuando las capacidades y
 * los campos se resolvían afuera; ahora entran por la puerta principal y
 * `motivoSinTablero` los tiene a mano. Un insumo menos que pasar es una forma
 * menos de pasarlo mal: el objeto viajaba con las capacidades **crudas**, y
 * cruzadas con el módulo dueño no dicen lo mismo.
 *
 * Es una función y no un `useMemo` para poder probar todo esto sin montar la
 * aplicación entera.
 */
export function resolveSurfaces(
  granted: readonly ModuleId[],
  hiddenNav: readonly ModuleId[],
  hiddenLeadTabs: readonly LeadTabId[],
  grantedCapabilities?: readonly CapabilityId[] | null,
  enabledLeadFields?: readonly LeadFieldId[] | null,
  leadsSource: LeadsSource = DEFAULT_LEADS_SOURCE,
): ResolvedSurfaces {
  const modules = resolveEnabledModules(granted);
  // `?? []` porque el frontend y la API se despliegan por separado: entre un
  // deploy y el otro la respuesta no trae estos campos todavía. Ausente tiene
  // que caer del lado de mostrar — esconder por accidente es el error que no se
  // reporta, porque nadie extraña una pestaña que no sabe que existe.
  //
  // Acá se filtraba la base, cuando estaba siempre. Se fue con la inyección
  // (2026-09-24, ver `BASE_MODULE_IDS`): un módulo que se puede apagar entero
  // también se puede sacar del menú. El servidor filtraba igual y dejó de
  // hacerlo en el mismo cambio (`map_tenant`, `hiddenNavModules`).
  const nav = new Set(hiddenNav ?? []);
  const tabs = new Set(hiddenLeadTabs ?? []);
  const capabilities = resolveCapabilities(grantedCapabilities, modules);
  const leadFields = resolveCampos(enabledLeadFields, modules, capabilities);

  const enabledModules = MODULE_LIST.filter((module) => modules.has(module.id)).sort(
    (a, b) => GROUP_RANK[grupoDelMenu(a)] - GROUP_RANK[grupoDelMenu(b)] || a.order - b.order,
  );

  // Sólo tiene sentido preguntarlo si la organización TIENE Leads: sin el módulo
  // la respuesta ya es otra —«no está habilitado»— y contestar «le falta el
  // campo etapa» sería explicar la segunda cerradura de una puerta que no está.
  //
  // Y se le pasan las llaves y los campos YA DESCONTADOS, que es lo que hay acá:
  // son los mismos que mira todo lo demás, así que el tablero no puede contestar
  // sobre una configuración distinta de la que ve el resto de la pantalla. El
  // descuento no lo cambia de opinión —`leads.kanban` es de `leads`, y sin ese
  // módulo esta rama ni se entra— pero tampoco lo hace dos veces.
  const sinTablero = modules.has('leads')
    ? motivoSinTablero(capabilities, leadFields, leadsSource)
    : null;

  // El orden de las preguntas es el de la causa: el módulo sostiene a la
  // capacidad, la capacidad y el módulo sostienen al campo, y la pestaña es la
  // superficie de un módulo. Así el motivo que sale es la RAÍZ y no un síntoma
  // — decir «falta el campo company» cuando lo que falta es el módulo Empresas
  // manda a arreglar la llave equivocada.
  const motivoDeProducto = (requisito: Requisito): MotivoDeProducto | null => {
    const { modulo, capacidad, campo, pestana } = requisito;
    if (modulo !== undefined && !modules.has(modulo)) return { eje: 'modulo', id: modulo };
    // LEADS SIN TABLERO ENTRA POR ACÁ, y no por un filtro aparte.
    //
    // «Leads es el embudo, y sólo el embudo»: sacada la vista tabla, una
    // organización que no puede tener tablero no tiene una pantalla peor, no
    // tiene ninguna. Eso se decidía en un `oculto()` propio que sólo miraba el
    // menú, y por eso el menú lo sabía y nadie más: el riel de la conversación
    // seguía dibujando su sección «Lead» —una lista de enlaces a una ruta que
    // contesta «sin acceso»— y `/` seguía teniendo que acordarse a mano.
    //
    // Puesta acá, la respuesta es la misma para las cuatro superficies del
    // cascarón y para cualquier otra que dependa de Leads, porque todas
    // preguntan por el mismo lugar. Va DESPUÉS del módulo a propósito: con
    // `leads` apagado gana «no está habilitado», que es la puerta de más
    // afuera; explicar la segunda cerradura de una puerta que no está sería
    // enseñar un vocabulario que no es de esta organización (§18.1).
    if (modulo === 'leads' && sinTablero !== null) {
      return sinTablero === 'campo'
        ? { eje: 'campo', id: 'stage' }
        : { eje: 'capacidad', id: llaveDelTablero(capabilities) };
    }
    if (capacidad !== undefined && !capabilities.has(capacidad)) {
      return { eje: 'capacidad', id: capacidad };
    }
    if (campo !== undefined && !leadFields.has(campo)) return { eje: 'campo', id: campo };
    if (pestana !== undefined && !leadTabVisible(pestana, modules, tabs)) {
      return { eje: 'pestana', id: pestana };
    }
    return null;
  };

  const muestra = (requisito: Requisito): boolean => motivoDeProducto(requisito) === null;

  // Las dos razones de no ofrecer un módulo en el cascarón, y son distintas:
  // `hiddenNavModules` es una PREFERENCIA —la pantalla existe y su URL responde,
  // sólo no la ofrecemos— y lo que decide `muestra` es que no hay pantalla que
  // dibujar. Se suman acá y en ningún otro lado, que es lo que hace que el menú,
  // los atajos y la paleta no puedan contestar distinto.
  const ofrecido = (id: ModuleId): boolean => !nav.has(id) && muestra({ modulo: id });

  return {
    modules,
    capabilities,
    leadFields,
    enabledModules,
    navModules: enabledModules.filter((module) => ofrecido(module.id)),
    showsInNav: ofrecido,
    showsLeadTab: (id) => leadTabVisible(id, modules, tabs),
    muestra,
    motivoDeProducto,
    accion: (accion) => resolverAccion(accion, muestra),
    sinTablero,
  };
}

/**
 * Los módulos que un cliente puede tener hoy: todos menos los no construidos y
 * los retirados.
 *
 * Las dos exclusiones son distintas por dentro —una nunca existió, la otra ya
 * no se ofrece— y la misma acá afuera: ninguna de las dos se puede encender.
 */
export const AVAILABLE_MODULE_IDS: ModuleId[] = MODULE_IDS.filter((id) => seOfrece(MODULES[id]));

/**
 * Resolve a raw entitlement list into the set that can actually render.
 *
 * A module whose `requires` are not all present is dropped rather than shown
 * broken — a client with `calls` but no `contacts` would otherwise get a screen
 * full of dangling references.
 *
 * Un módulo `unavailable` se cae acá para todos, tenga o no el cliente el id
 * escrito en su configuración. Es a propósito que sea este filtro y no el valor
 * por defecto: si sólo cambiara el default, los cuatro tenants de dev que ya
 * tienen su lista escrita seguirían con la maqueta encendida, y encenderla de
 * nuevo sería un clic.
 *
 * Un módulo `retirado` se cae por lo mismo y en el mismo lugar, y por eso el
 * retiro no necesita tocar la configuración de una sola organización: el id
 * sigue escrito donde estaba, el servidor lo sigue viendo —el Resumen se apoya
 * en eso—, y esta pantalla deja de ofrecerlo. Volver atrás es borrar una línea
 * de `MODULES`, no editar N tenants.
 */
export function resolveEnabledModules(granted: readonly ModuleId[]): Set<ModuleId> {
  // Lo que la organización tiene, y nada más. Acá se le sumaba la base, que
  // entraba pidiera lo que pidiera: se fue el 2026-09-24 con el resto de la
  // inyección (ver `BASE_MODULE_IDS`), y el servidor la sacó del mismo lugar
  // (`_enabled_modules` en `tenant_mapping.py`).
  const wanted = new Set<ModuleId>(granted);
  for (const id of wanted) {
    if (!seOfrece(MODULES[id])) wanted.delete(id);
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of wanted) {
      const requires = MODULES[id].requires;
      if (requires?.some((dep) => !wanted.has(dep))) {
        wanted.delete(id);
        changed = true;
      }
    }
  }
  return wanted;
}
